import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Typography, Chip, IconButton, TextField, Checkbox, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell,
  Button, Divider,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";
import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";
import mpDemandConstraintService from "@plannel/services/mp/mp-demand-constraint-service";
import { FS } from "@plannel/utils/font-util";

const { snackBarMessageColor } = ColorUtil;
const { mpDemandType } = CodeUtil;

// 공급계획 수요타입 → z_mp_demand_alloc.order_type 저장 코드.
// MP DemandSource 규약(DP/RP/PO)에 맞춘다: 수요계획=DP, 보충계획=RP, 생산 주문=PO.
const ORDER_TYPE_BY_DEMAND_TYPE = {
  [mpDemandType.FORECAST]: "DP",
  [mpDemandType.REPLENISHMENT]: "RP",
  [mpDemandType.PRODORDER]: "PO",
};
// 저장/조회 키에 쓸 order_type — 알 수 없는 수요타입은 null(서버가 "" 로 저장한다).
// 호출 측(수요 목록)에서도 같은 키로 저장 여부를 조회하므로 export 한다.
export const resolveOrderType = (o) => ORDER_TYPE_BY_DEMAND_TYPE[o?.demandType] ?? null;

/*
 * 공급계획 수요(선택된 1건)에 대해 재고/재공/공정/자원을 조회하여 등록하고
 * 우선순위·수량을 설정하는 팝업. 디자인 목업(공급계획 수요 제약 설정 화면)을
 * saas-web 설정 모달 컨벤션(BaseConfigModal)에 맞춰 재구성했다.
 *
 * 재고 탭은 /api/mp-demand-constraint/inventory-data 로 실제 조회한다.
 * ⚠ 재공/공정/자원 후보는 아직 목업이고, 저장은 로컬 draft를 닫는 동작만 한다.
 */

const ROW_H = 40;

/*
 * py 0.75 로 높이를 줄인 small 입력칸의 라벨 세로 위치 보정.
 * MUI 는 아직 안 올라간(플레이스홀더처럼 보이는) outlined 라벨을 `translate(14px, 9px)` 로 놓는데,
 * 그 9px 은 기본 패딩(8.5px) 기준이다. 여기처럼 py 를 6px 로 줄이면 라벨만 그대로 남아 아래로 처져 보인다
 * (Route 셀렉트에서 "글자가 너무 아래"라는 지적을 받았다). 줄인 패딩에 맞춰 6px 로 내린다.
 * 라벨이 올라간(shrink) 상태는 테두리 위에 놓이므로 건드리지 않는다.
 */
const COMPACT_LABEL_SX = {
  "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 6px) scale(1)" },
};

// 재고 유형 표시 라벨 — InventoryWipTracking 과 동일한 표기를 쓴다(코드 미등록 값은 코드 그대로 노출)
const INV_TYPE_LABEL = {
  ON_HAND: "On-Hand",
  IN_TRANSIT: "In-Transit",
  WIP: "WIP",
};

// 일자는 문자열(ISO)로도, epoch 숫자로도 올 수 있다. json-bigint가 큰 정수를 BigInt로
// 파싱하므로 Date 생성자에 그대로 넘기면 TypeError — 항상 Number로 좁혀서 변환한다.
const toDateText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return dateUtils.formatDate(typeof value === "bigint" ? Number(value) : value, "yyyy-MM-dd");
};

// 값이 없는 코드 컬럼은 화면에 "-" 로 표시한다 — 저장할 때는 그 플레이스홀더를 코드로 굳히지 않고 null 로 되돌린다.
const toSavedCode = (value) => (value && value !== "-" ? value : null);

// MyBatis resultType="map" 응답은 컬럼 라벨(snake_case)이 그대로 키가 된다.
// txnDate   현재고를 집계한 실적 일자 — 이동중 재고는 빈 값
// baseDate  이동중 재고를 집계한 입고 계획 기준일자 — 현재고는 빈 값
// availDate 재고를 쓸 수 있게 되는 일자 — 현재고는 실적 일자, 이동중은 도착 일자
const mapInventoryRow = (row) => ({
  // id 는 화면 식별용(원천 테이블이 둘이라 유형까지 붙여 구분), invId 는 저장 시 그대로 되돌려 줄 원본 값(BigInt — 정밀도 보존)
  id: `${row?.inv_type ?? ""}-${row?.inv_id ?? ""}`,
  invId: row?.inv_id ?? null,
  invType: INV_TYPE_LABEL[row?.inv_type] ?? row?.inv_type ?? "-",
  locCd: row?.loc_cd ?? "-",
  itemCd: row?.item_cd ?? "-",
  txnDate: toDateText(row?.txn_date),
  baseDate: toDateText(row?.base_date),
  availDate: toDateText(row?.avail_date),
  avail: Number(row?.avail_qty) || 0,
});

// 재공 코드(wip_cd)는 원천에서 NULL 일 수 있다 — 표시용이고, 할당 식별은 계속 wip_id 로 한다
const mapWipRow = (row) => ({
  id: `WIP-${row?.wip_id ?? ""}`,
  wipId: row?.wip_id ?? null,
  wipCd: row?.wip_cd ?? "-",
  locCd: row?.loc_cd ?? "-",
  workcenter: row?.wc_cd ?? "-",
  itemCd: row?.item_cd ?? "-",
  route: row?.route_cd ?? "-",
  resource: row?.res_cd ?? "-",
  prodDate: toDateText(row?.prod_date),
  avail: Number(row?.avail_qty) || 0,
});

const mapRouteRow = (row) => ({
  code: row?.route_cd ?? "-",
  name: row?.route_name ?? "-",
  locCd: row?.loc_cd ?? "-",
  resCnt: Number(row?.res_cnt) || 0,
});

const mapResourceRow = (row) => ({
  // 같은 자원이 여러 공정에 지정될 수 있어 공정+자원으로 식별한다
  id: `${row?.route_cd ?? ""}::${row?.res_cd ?? ""}`,
  route: row?.route_cd ?? "-",
  code: row?.res_cd ?? "-",
  name: row?.res_name ?? "-",
  workcenter: row?.wc_cd ?? "-",
  locCd: row?.loc_cd ?? "-",
});

// 저장된 할당(alloc_obj.inventory) 1건 → 화면 행.
// 현재 스냅샷에 같은 재고(invId)가 있으면 그 행의 정보(유형/일자/가용 수량)를 쓰고,
// 없으면(스냅샷이 바뀐 경우) 저장 당시 값만으로 행을 만들어 이전 할당이 사라지지 않게 한다.
const restoreInventoryRow = (saved, catalogByInvId) => {
  const invId = saved?.invId ?? null;
  const current = catalogByInvId.get(String(invId));
  const base = current ?? {
    id: `SAVED-${invId}`,
    invId,
    invType: "-",
    locCd: saved?.locCd ?? "-",
    itemCd: saved?.itemCd ?? "-",
    txnDate: "",
    baseDate: "",
    availDate: toDateText(saved?.availDate),
    avail: Number(saved?.availQty) || 0,
  };

  return { ...base, qty: Number(saved?.allocQty) || 0, active: saved?.activeFlg !== false };
};

// 저장된 공정/자원 지정 → 화면 행. 저장된 배열 순서가 곧 우선순위라 그대로 복원한다.
const restoreRouteRow = (saved, catalogByCode) => {
  const routeCd = saved?.routeCd ?? "";
  const current = catalogByCode.get(routeCd);
  const base = current ?? {
    code: routeCd || "-",
    name: saved?.routeName ?? "-",
    locCd: saved?.locCd ?? "-",
    resCnt: Number(saved?.resCnt) || 0,
  };

  return { ...base, active: saved?.activeFlg !== false };
};

// 저장 값에는 자원 명칭이 없다(코드만 저장) — 공정 코드로 다시 조회한 자원 마스터에서 채운다.
// 마스터에서도 못 찾으면(자원 비활성/삭제) 코드만으로 행을 만든다.
const restoreResourceRow = (saved, catalogById) => {
  const id = `${saved?.routeCd ?? ""}::${saved?.resCd ?? ""}`;
  const current = catalogById.get(id);
  const base = current ?? {
    id,
    route: saved?.routeCd ?? "-",
    code: saved?.resCd ?? "-",
    name: saved?.resourceName ?? "-",
    workcenter: saved?.wcCd ?? "-",
    locCd: saved?.locCd ?? "-",
  };

  return { ...base, active: saved?.activeFlg !== false };
};

// 저장된 할당(alloc_obj.wip) 1건 → 화면 행. 재고와 같은 방식으로 현재 조회 결과와 wipId 로 맞춘다.
const restoreWipRow = (saved, catalogByWipId) => {
  const wipId = saved?.wipId ?? null;
  const current = catalogByWipId.get(String(wipId));
  const base = current ?? {
    id: `SAVED-WIP-${wipId}`,
    wipId,
    wipCd: saved?.wipCd ?? "-",
    locCd: saved?.locCd ?? "-",
    workcenter: saved?.wcCd ?? "-",
    itemCd: saved?.itemCd ?? "-",
    route: saved?.routeCd ?? "-",
    resource: saved?.resCd ?? "-",
    prodDate: toDateText(saved?.prodDate),
    avail: Number(saved?.availQty) || 0,
  };

  return { ...base, qty: Number(saved?.allocQty) || 0, active: saved?.activeFlg !== false };
};

// 탭별 규격
// mode        "sum": 수량을 주문 수량에 배분(재고/재공), "none": 지정 여부만(공정/자원)
// usePriority 우선순위(드래그 순서)를 쓰는 탭 — 재고/재공은 Pegging이라 순서에 의미가 없어 미사용
// useBaseDate 조회 팝업에서 기준일자를 지정해 해당 시점 기준으로 후보를 조회하는 탭
// remote      후보를 API로 조회하는 탭(catalog 없음) — 그 외는 목업 catalog 사용
// bomScoped   요청 품목의 BOM 구성품(반제품/원자재)이 조회 대상인 탭
// hintKey     좌측 레일 하단에 표시할 탭 설명 메시지 키
// allocByBom  할당 수량을 BOM 소요량 기준으로 다루는 탭 — 요약은 완제품 환산 수량,
//             자재별 할당 상한은 (완제품 주문 수량 × 단위 소요량)
// lotKeyField 재고/재공 lot 을 식별하는 필드 — 같은 수요 버전의 다른 수요가 그 lot 에 이미
//             할당한 수량(타 수요 할당)을 이 키로 찾아 가용 수량에서 차감한다
// 컬럼 type "seq": 저장 값이 아니라 화면 표시 순번(단순 시퀀스)
// 컬럼 availNet 원 수량에서 타 수요 할당을 뺀, 이 수요가 쓸 수 있는 가용 수량(withLotAvail 이 채운다)
const META = {
  inventory: {
    // z_inventory_txn 의 id(inv_id) 기준으로 할당 — 재고 코드가 없어 id로만 식별(재고 번호는 표시용 순번)
    labelKey: "inventory", icon: Inventory2OutlinedIcon, mode: "sum", keyField: "id",
    usePriority: false, useBaseDate: true, remote: true, bomScoped: true, allocByBom: true,
    lotKeyField: "invId",
    hintKey: "MSG_InventorySnapshotRule",
    cols: [
      { key: "seq", labelKey: "invNo", type: "seq" },
      { key: "invType", labelKey: "invType" },
      { key: "locCd", labelKey: "location" },
      { key: "itemCd", labelKey: "itemCd" },
      { key: "availDate", labelKey: "availDate" },
      { key: "availNet", labelKey: "availQty", align: "right" },
      { key: "otherQty", labelKey: "otherDemandAllocQty", align: "right" },
      { key: "qty", labelKey: "allocatedQty", type: "num", align: "right" },
      { key: "active", labelKey: "activeFlg", type: "toggle" },
    ],
  },
  wip: {
    // z_wip 실적 데이터의 id 기준으로 할당 — 재공 코드가 없어 id로만 식별(재공 번호는 표시용 순번)
    // 완제품 자신과 반제품 재공이 함께 조회되므로 할당 수량도 완제품 환산으로 다룬다
    labelKey: "wip", icon: ConstructionOutlinedIcon, mode: "sum", keyField: "id",
    usePriority: false, remote: true, allocByBom: true,
    lotKeyField: "wipId",
    hintKey: "MSG_WipPlanScope",
    cols: [
      { key: "seq", labelKey: "wipNo", type: "seq" },
      { key: "locCd", labelKey: "location" },
      { key: "workcenter", labelKey: "workcenter" },
      { key: "itemCd", labelKey: "item" },
      { key: "route", labelKey: "route" },
      { key: "resource", labelKey: "resource" },
      { key: "prodDate", labelKey: "prodDate" },
      { key: "wipCd", labelKey: "wipCd" },
      { key: "availNet", labelKey: "availQty", align: "right" },
      { key: "otherQty", labelKey: "otherDemandAllocQty", align: "right" },
      { key: "qty", labelKey: "allocatedQty", type: "num", align: "right" },
      { key: "active", labelKey: "activeFlg", type: "toggle" },
    ],
  },
  route: {
    // 주문 품목의 생산 공정 — 거점에 생산 자원이 1개 이상 있는 공정(대체 공정 포함)만 조회된다
    labelKey: "route", icon: AccountTreeOutlinedIcon, mode: "none", keyField: "code", usePriority: true,
    remote: true, hintKey: "MSG_RouteAltPriority",
    cols: [
      { key: "code", labelKey: "routeCd" },
      { key: "name", labelKey: "routeName" },
      { key: "locCd", labelKey: "location" },
      { key: "resCnt", labelKey: "resCnt", align: "right" },
      { key: "active", labelKey: "activeFlg", type: "toggle" },
    ],
  },
  resource: {
    // 대체 자원(자원 2개 이상)이 있는 공정을 골라 그 공정의 자원을 조회·지정한다.
    // 같은 자원이 여러 공정에 걸릴 수 있어 공정+자원 조합(id)으로 식별한다
    labelKey: "resource", icon: PrecisionManufacturingOutlinedIcon, mode: "none", keyField: "id", usePriority: true,
    remote: true, needsRoute: true, hintKey: "MSG_ResourceByRoutePriority",
    cols: [
      { key: "route", labelKey: "route" },
      { key: "code", labelKey: "resCd" },
      { key: "name", labelKey: "resName" },
      { key: "workcenter", labelKey: "workcenter" },
      { key: "locCd", labelKey: "location" },
      { key: "active", labelKey: "activeFlg", type: "toggle" },
    ],
  },
};
const TAB_KEYS = Object.keys(META);
/*
 * 할당 수량을 BOM 소요량 기준으로 다루는 탭(재고·재공).
 * 두 탭의 조회 대상은 반제품에서 겹치므로(재고=반제품+원자재, 재공=완제품+반제품)
 * 자재별 소요량은 탭별로 따로 두지 않고 두 탭이 하나의 풀로 나눠 쓴다 — 같은 반제품을
 * 재고와 재공에서 각각 소요량 전량 할당하면 중복 할당이 되어 엔진에 과다 페깅된다.
 */
const BOM_TABS = TAB_KEYS.filter((k) => META[k].allocByBom);

// 소수 자리 부동소수 오차로 "소요량 초과"가 오탐되지 않게 비교 전에 자리수를 맞춘다(서버 검증과 동일 기준)
const QTY_EPSILON = 1e-6;

const MpDemandConstraintModal = ({ open, onClose, order, parPlanVerId }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(TAB_KEYS[0]);
  const [data, setData] = useState({ inventory: [], wip: [], route: [], resource: [] });
  const [sel, setSel] = useState({ inventory: {}, wip: {}, route: {}, resource: {} });
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupSel, setLookupSel] = useState({});
  const [lookupQuery, setLookupQuery] = useState("");
  // 조회 기준일자 — 재고처럼 시점에 따라 가용량이 달라지는 탭에서 사용(기본값: 오늘)
  const [baseDate, setBaseDate] = useState(null);
  // API로 조회하는 후보(재고/재공) — null 은 "아직 조회 전"이라는 뜻으로,
  // 저장된 할당을 언제 복원할지 판단하는 데 쓴다
  const [remoteCatalog, setRemoteCatalog] = useState({ inventory: null, wip: null, route: null, resource: null });
  // 저장된 할당 내역 { inventory, wip, route, resource } — null 은 조회 전
  const [savedAlloc, setSavedAlloc] = useState(null);
  // 같은 수요 버전의 "다른" 수요들이 lot 에 이미 할당한 수량 { inventory: { [invId]: qty }, wip: { [wipId]: qty } }
  // — lot 하나가 여러 수요에 중복 할당되지 않도록 가용 수량에서 차감한다
  const [otherAlloc, setOtherAlloc] = useState({ inventory: {}, wip: {} });
  // 저장된 자원 지정의 표시명 해석용 자원 마스터 Map(id `공정::자원` → 후보 행) — null 은 조회 전.
  // 자원 탭 후보(remoteCatalog.resource)는 사용자가 공정을 골라야 조회되므로 복원에는 쓸 수 없어,
  // 저장된 행의 공정 코드로 따로 조회한다.
  const [savedResourceCatalog, setSavedResourceCatalog] = useState(null);
  // 생산 자원 탭에서 고른 공정 — 대체 자원이 있는 공정 목록 중 하나
  const [resourceRoutes, setResourceRoutes] = useState([]);
  const [selectedRouteCd, setSelectedRouteCd] = useState("");
  // 완제품 1개당 자재 소요량 { [itemCd]: unitQty } — 할당 수량 환산·상한 계산에 쓴다
  const [bomRatio, setBomRatio] = useState({});
  // 조회 상태는 탭별로 관리한다(재고/재공이 동시에 조회되므로 공용 플래그면 서로 덮어쓴다)
  const [remoteLoading, setRemoteLoading] = useState({});
  const [remoteError, setRemoteError] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const uidRef = useRef(0);
  const dragIndexRef = useRef(null);
  // 저장된 할당은 팝업을 열 때 한 번만 복원한다(이후 기준일자 변경으로 재조회돼도 사용자 편집을 덮지 않음)
  const restoredRef = useRef(false);

  /*
   * 초기화는 "열 때"가 아니라 "닫을 때" 한다.
   * 열 때 초기화하면 같은 커밋에서 실행되는 아래 복원 effect 가 아직 반영되지 않은
   * 직전 세션의 savedAlloc/후보 목록(스테일 값)을 보고 복원을 끝내버린다.
   * 그러면 복원 플래그가 소진되어, 정작 새로 조회한 결과가 도착해도 반영되지 않는다.
   * (증상: 저장 후 다시 열면 저장 내역이 안 보이고, 닫고 한 번 더 열어야 보인다)
   */
  useEffect(() => {
    if (open) {
      // 다시 열 때 직전 저장 결과 토스트가 남아 있지 않도록 정리
      setSnackInfo({ open: false, severity: "", content: "" });
      return;
    }

    setActiveTab(TAB_KEYS[0]);
    setData({ inventory: [], wip: [], route: [], resource: [] });
    setSel({ inventory: {}, wip: {}, route: {}, resource: {} });
    setLookupOpen(false);
    setLookupSel({});
    setLookupQuery("");
    setBaseDate(new ZDate());
    setRemoteCatalog({ inventory: null, wip: null, route: null, resource: null });
    setSavedAlloc(null);
    setOtherAlloc({ inventory: {}, wip: {} });
    setSavedResourceCatalog(null);
    setResourceRoutes([]);
    setSelectedRouteCd("");
    restoredRef.current = false;
    setBomRatio({});
    setRemoteLoading({});
    setRemoteError({});
    setSaving(false);
  }, [open, order]);

  // 완제품 기준 자재 소요량 조회 — 등록/할당 화면 전체에서 쓰이므로 팝업을 열 때 한 번 읽는다
  useEffect(() => {
    if (!open || !order?.itemCd) return undefined;

    let canceled = false;
    mpDemandConstraintService.getBomRatio({ itemCd: order.itemCd }).then((res) => {
      if (canceled) return;
      // 구성품이 없으면 204(No Content) — 환산 정보 없음으로 둔다
      const results = res?.data?.results ?? [];
      // 완제품 자신은 API 응답에 없다(구성품만 반환) — 재공 탭에 완제품 재공이 섞이므로 1:1 로 넣어준다
      const next = { [order.itemCd]: 1 };
      results.forEach((row) => {
        const itemCd = row?.item_cd;
        const unitQty = Number(row?.unit_qty) || 0;
        if (itemCd && unitQty > 0) next[itemCd] = unitQty;
      });
      setBomRatio(next);
    }).catch(() => {
      // 구성품 환산 정보를 못 읽어도 완제품 재공은 1:1 로 다룰 수 있다
      if (!canceled) setBomRatio({ [order.itemCd]: 1 });
    });

    return () => { canceled = true; };
  }, [open, order]);

  // 저장된 할당 내역 조회 — 이전에 저장한 할당을 초기값으로 복원한다.
  // 수요 버전이 없으면(MP 단독) 저장 자체가 불가하므로 조회도 건너뛴다.
  useEffect(() => {
    if (!open) return undefined;

    const dueDate = dateUtils.formatDate(order?.dueDate, "yyyy-MM-dd");
    if (!parPlanVerId || !order?.locCd || !order?.itemCd || !dueDate) {
      setSavedAlloc({ inventory: [], wip: [], route: [], resource: [] });
      return undefined;
    }

    let canceled = false;
    mpDemandConstraintService.getAllocation({
      parPlanVerId,
      locCd: order.locCd,
      itemCd: order.itemCd,
      dueDate,
      // 수요타입 기준 order_type(DP/RP/PO) — 저장 시와 동일한 규칙으로 키를 맞춘다.
      orderType: resolveOrderType(order),
      reqLocCd: order.reqLocCd ?? null,
      customerCd: order.customerCd ?? null,
    }).then((res) => {
      // 저장 이력이 없으면 204(No Content)
      const results = res?.data?.results ?? {};
      if (canceled) return;
      setSavedAlloc({
        inventory: results.inventory ?? [],
        wip: results.wip ?? [],
        route: results.route ?? [],
        resource: results.resource ?? [],
      });
    }).catch((e) => {
      // 복원 실패는 신규 설정으로 진행 — 저장 자체를 막지는 않는다
      if (!canceled) setSavedAlloc({ inventory: [], wip: [], route: [], resource: [] });
      debug(e);
    });

    return () => { canceled = true; };
  }, [open, order, parPlanVerId]);

  /*
   * 같은 수요 버전의 다른 수요들이 재고/재공 lot 에 이미 할당한 수량 조회.
   * lot 1건을 여러 수요가 나눠 갖지 않도록 가용 수량에서 이 값을 뺀다(팝업을 열 때의 스냅샷 —
   * 그 사이 다른 사용자가 같은 lot 을 채우면 저장 시점에 서버가 400 으로 거부한다).
   * 저장된 할당 조회와 같은 키/조건이라 조회 시점도 함께 맞춘다.
   */
  useEffect(() => {
    if (!open) return undefined;

    const dueDate = dateUtils.formatDate(order?.dueDate, "yyyy-MM-dd");
    if (!parPlanVerId || !order?.locCd || !order?.itemCd || !dueDate) {
      setOtherAlloc({ inventory: {}, wip: {} });
      return undefined;
    }

    let canceled = false;
    mpDemandConstraintService.getOtherAllocation({
      parPlanVerId,
      locCd: order.locCd,
      itemCd: order.itemCd,
      dueDate,
      orderType: resolveOrderType(order),
      reqLocCd: order.reqLocCd ?? null,
      customerCd: order.customerCd ?? null,
    }).then((res) => {
      // 다른 수요의 할당이 없으면 204(No Content)
      const results = res?.data?.results ?? {};
      if (canceled) return;
      setOtherAlloc({ inventory: results.inventory ?? {}, wip: results.wip ?? {} });
    }).catch((e) => {
      // 차감 정보를 못 읽어도 설정 자체는 진행 — 초과분은 저장 시 서버가 걸러준다
      if (!canceled) setOtherAlloc({ inventory: {}, wip: {} });
      debug(e);
    });

    return () => { canceled = true; };
  }, [open, order, parPlanVerId]);

  const meta = META[activeTab];
  const rows = data[activeTab];
  const curSel = sel[activeTab];
  const orderQty = Number(order?.demandQty) || 0;

  const lookupLoading = !!remoteLoading[activeTab];
  const lookupError = remoteError[activeTab] ?? "";

  // 재고 후보 조회 — 탭에서 고른 기준일자와 대상 주문(거점/품목) 기준으로 API를 호출한다.
  // 기준일자를 탭에서 지정하므로 팝업을 열기 전에 미리 조회해 둔다.
  // 서버가 품목의 BOM 을 전개하므로 여기서는 주문 거점/품목 코드만 넘긴다(모두 필수 파라미터).
  useEffect(() => {
    if (!open) return undefined;
    if (!order?.locCd || !order?.itemCd) return undefined;

    // 날짜를 직접 입력하는 중이면(미완성 값) 호출하지 않고 이전 결과를 유지한다
    const baseDateParam = dateUtils.formatDate(baseDate, "yyyy-MM-dd");
    if (!baseDateParam) return undefined;

    let canceled = false;
    setRemoteLoading((s) => ({ ...s, inventory: true }));
    setRemoteError((s) => ({ ...s, inventory: "" }));

    mpDemandConstraintService.getInventoryData({
      baseDate: baseDateParam,
      locCd: order.locCd,
      itemCd: order.itemCd,
    }).then((res) => {
      if (canceled) return;
      // 조회 결과가 없으면 204(No Content) — body가 없으므로 0건으로 처리한다
      const results = res?.data?.results ?? [];
      setRemoteCatalog((c) => ({ ...c, inventory: results.map(mapInventoryRow) }));
    }).catch(() => {
      if (canceled) return;
      setRemoteCatalog((c) => ({ ...c, inventory: [] }));
      setRemoteError((s) => ({ ...s, inventory: t("MSG_ServerError") }));
    }).finally(() => {
      if (!canceled) setRemoteLoading((s) => ({ ...s, inventory: false }));
    });

    return () => { canceled = true; };
  }, [open, baseDate, order, t]);

  // 재공 후보 조회 — 거점·품목만 기준(기준일자 개념이 없다)
  useEffect(() => {
    if (!open) return undefined;
    if (!order?.locCd || !order?.itemCd) return undefined;

    let canceled = false;
    setRemoteLoading((s) => ({ ...s, wip: true }));
    setRemoteError((s) => ({ ...s, wip: "" }));

    mpDemandConstraintService.getWipData({
      locCd: order.locCd,
      itemCd: order.itemCd,
    }).then((res) => {
      if (canceled) return;
      const results = res?.data?.results ?? [];
      setRemoteCatalog((c) => ({ ...c, wip: results.map(mapWipRow) }));
    }).catch(() => {
      if (canceled) return;
      setRemoteCatalog((c) => ({ ...c, wip: [] }));
      setRemoteError((s) => ({ ...s, wip: t("MSG_ServerError") }));
    }).finally(() => {
      if (!canceled) setRemoteLoading((s) => ({ ...s, wip: false }));
    });

    return () => { canceled = true; };
  }, [open, order, t]);

  // 생산 공정 후보 조회 — 주문 품목의 공정 중 거점에 생산 자원이 있는 것만 내려온다
  useEffect(() => {
    if (!open) return undefined;
    if (!order?.locCd || !order?.itemCd) return undefined;

    let canceled = false;
    setRemoteLoading((s) => ({ ...s, route: true }));
    setRemoteError((s) => ({ ...s, route: "" }));

    mpDemandConstraintService.getRouteData({
      locCd: order.locCd,
      itemCd: order.itemCd,
    }).then((res) => {
      if (canceled) return;
      const results = res?.data?.results ?? [];
      setRemoteCatalog((c) => ({ ...c, route: results.map(mapRouteRow) }));
    }).catch(() => {
      if (canceled) return;
      setRemoteCatalog((c) => ({ ...c, route: [] }));
      setRemoteError((s) => ({ ...s, route: t("MSG_ServerError") }));
    }).finally(() => {
      if (!canceled) setRemoteLoading((s) => ({ ...s, route: false }));
    });

    return () => { canceled = true; };
  }, [open, order, t]);

  // 생산 자원을 지정할 공정 목록 조회 — 대체 자원(자원 2개 이상)이 있는 공정만 선택할 수 있다
  useEffect(() => {
    if (!open || !order?.locCd || !order?.itemCd) return undefined;

    let canceled = false;
    mpDemandConstraintService.getResourceRouteData({
      locCd: order.locCd,
      itemCd: order.itemCd,
    }).then((res) => {
      if (canceled) return;
      const results = res?.data?.results ?? [];
      setResourceRoutes(results.map(mapRouteRow));
    }).catch((e) => {
      if (!canceled) setResourceRoutes([]);
      debug(e);
    });

    return () => { canceled = true; };
  }, [open, order]);

  // 선택한 공정의 생산 자원 후보 조회
  useEffect(() => {
    if (!open || !order?.locCd) return undefined;

    // 공정을 먼저 선택해야 자원을 조회할 수 있다
    if (!selectedRouteCd) {
      setRemoteCatalog((c) => ({ ...c, resource: [] }));
      return undefined;
    }

    let canceled = false;
    setRemoteLoading((s) => ({ ...s, resource: true }));
    setRemoteError((s) => ({ ...s, resource: "" }));

    mpDemandConstraintService.getResourceData({
      locCd: order.locCd,
      routeCdList: [selectedRouteCd],
    }).then((res) => {
      if (canceled) return;
      const results = res?.data?.results ?? [];
      setRemoteCatalog((c) => ({ ...c, resource: results.map(mapResourceRow) }));
    }).catch(() => {
      if (canceled) return;
      setRemoteCatalog((c) => ({ ...c, resource: [] }));
      setRemoteError((s) => ({ ...s, resource: t("MSG_ServerError") }));
    }).finally(() => {
      if (!canceled) setRemoteLoading((s) => ({ ...s, resource: false }));
    });

    return () => { canceled = true; };
  }, [open, order, selectedRouteCd, t]);

  // 저장된 자원 지정의 명칭 해석 — 저장 값은 코드뿐이라 저장된 공정 코드로 자원 마스터를 다시 조회한다.
  // 사용자가 고른 공정(selectedRouteCd)과 무관하게 저장된 행 전체를 대상으로 하므로 별도 상태에 담는다.
  useEffect(() => {
    if (!open || savedAlloc === null) return undefined;

    const routeCdList = [...new Set((savedAlloc.resource ?? []).map((r) => r?.routeCd).filter(Boolean))];
    // 저장된 자원이 없으면 조회할 것도 없다 — 복원 effect 가 기다리지 않도록 빈 Map 으로 끝낸다
    if (!order?.locCd || routeCdList.length === 0) {
      setSavedResourceCatalog(new Map());
      return undefined;
    }

    let canceled = false;
    mpDemandConstraintService.getResourceData({
      locCd: order.locCd,
      routeCdList,
    }).then((res) => {
      if (canceled) return;
      const results = res?.data?.results ?? [];
      setSavedResourceCatalog(new Map(results.map(mapResourceRow).map((c) => [c.id, c])));
    }).catch((e) => {
      // 명칭을 못 읽어도 저장된 코드만으로 복원은 진행한다
      if (!canceled) setSavedResourceCatalog(new Map());
      debug(e);
    });

    return () => { canceled = true; };
  }, [open, order, savedAlloc]);

  // 저장된 할당 복원 — 후보 조회와 할당 이력 조회가 모두 끝난 뒤 한 번만 적용한다.
  // 저장 당시의 행을 현재 조회 결과와 키(invId/wipId/공정 코드/공정::자원)로 맞춰, 일자·수량은 최신 값을 쓴다.
  useEffect(() => {
    if (!open || restoredRef.current) return;
    if (savedAlloc === null || savedResourceCatalog === null) return;
    if (remoteCatalog.inventory === null || remoteCatalog.wip === null || remoteCatalog.route === null) return;

    restoredRef.current = true;
    const { inventory, wip, route, resource } = savedAlloc;
    if (!inventory.length && !wip.length && !route.length && !resource.length) return;

    const invByKey = new Map(remoteCatalog.inventory.map((c) => [String(c.invId), c]));
    const wipByKey = new Map(remoteCatalog.wip.map((c) => [String(c.wipId), c]));
    const routeByKey = new Map(remoteCatalog.route.map((c) => [c.code, c]));
    setData((d) => ({
      ...d,
      inventory: inventory.map((s) => restoreInventoryRow(s, invByKey)),
      wip: wip.map((s) => restoreWipRow(s, wipByKey)),
      route: route.map((s) => restoreRouteRow(s, routeByKey)),
      resource: resource.map((s) => restoreResourceRow(s, savedResourceCatalog)),
    }));
  }, [open, savedAlloc, savedResourceCatalog, remoteCatalog]);

  const setRows = (next) => setData((d) => ({ ...d, [activeTab]: next }));

  // 완제품 1개당 자재 소요량 / 주문 수량을 다 생산하는 데 필요한 자재 소요량
  const unitQtyOf = (itemCd) => Number(bomRatio?.[itemCd]) || 0;
  const reqQtyOf = (itemCd) => unitQtyOf(itemCd) * orderQty;

  /*
   * 자재별 할당 합계 — 재고·재공을 합친 총량 기준.
   * 현재 탭만 보면 같은 반제품에 두 탭이 각각 소요량 전량을 허용해 중복 할당이 되므로,
   * 상한·요약 모두 이 총량 맵을 기준으로 판단한다.
   * 비활성 행은 제외한다 — 엔진에서 unassignQty(할당 해제)로 쓰이는 별개 의미라 소요량을 차지하지 않는다.
   * byTab 은 "다른 탭이 이미 쓴 양"을 빼야 하는 자동 배분에서 쓴다.
   */
  const { total: allocByItem, byTab: allocByItemByTab } = useMemo(() => {
    const total = {};
    const byTab = {};
    BOM_TABS.forEach((tab) => {
      const sums = {};
      (data[tab] ?? []).forEach((r) => {
        if (!r.active) return;
        const qty = Number(r.qty) || 0;
        sums[r.itemCd] = (sums[r.itemCd] ?? 0) + qty;
        total[r.itemCd] = (total[r.itemCd] ?? 0) + qty;
      });
      byTab[tab] = sums;
    });
    return { total, byTab };
  }, [data]);

  const orderChips = useMemo(() => ([
    // 거래처/요청 거점은 NULL일 수 있는 항목 — 값이 없으면 칩 자체를 표시하지 않는다
    { label: t("customer"), value: order?.customerName },
    { label: t("location"), value: order?.locName ?? "-" },
    { label: t("reqLocName"), value: order?.reqLocName },
    { label: t("item"), value: order?.itemName ?? "-" },
    { label: t("dueDate"), value: order?.dueDate ? dateUtils.formatDate(order.dueDate, "yyyy-MM-dd") : "-" },
    { label: t("demandType"), value: order?.demandType ? GridUtils.gridValueL10N(order.demandType) : "-" },
    { label: t("orderQty"), value: order ? `${orderQty} ${order?.uomCd ?? ""}`.trim() : "-" },
  ].filter((c) => c.value !== null && c.value !== undefined && c.value !== "")), [order, orderQty, t]);

  const toggleRowSel = (id) => setSel((s) => ({ ...s, [activeTab]: { ...s[activeTab], [id]: !s[activeTab][id] } }));
  const allSelected = rows.length > 0 && rows.every((r) => curSel[r.id]);
  const toggleSelectAll = () => {
    const next = {};
    if (!allSelected) rows.forEach((r) => { next[r.id] = true; });
    setSel((s) => ({ ...s, [activeTab]: next }));
  };

  const removeSelected = () => {
    setRows(rows.filter((r) => !curSel[r.id]));
    setSel((s) => ({ ...s, [activeTab]: {} }));
  };

  const updateCell = (id, key, value) => setRows(rows.map((r) => (r.id === id ? { ...r, [key]: value } : r)));

  // 자재별로 다른 탭이 이미 할당한 수량 — 총량 기준이라 현재 탭이 채울 수 있는 몫은 그만큼 줄어든다
  const allocByOtherTabs = (itemCd) => BOM_TABS
    .filter((tab) => tab !== activeTab)
    .reduce((a, tab) => a + (allocByItemByTab[tab]?.[itemCd] ?? 0), 0);

  /*
   * lot 1건의 "타 수요 할당"(otherQty)과 그만큼 뺀 가용 수량(availNet)을 행에 붙인다 —
   * 다른 수요가 이미 쓴 몫은 이 수요가 쓸 수 없으므로, 표시·입력 상한·자동 배분이 모두 이 값을 쓴다.
   * 계산값은 화면 표시용이라 data 에 굳히지 않고 렌더링 시점에만 덧붙인다
   * (저장 payload 의 availQty 는 계속 lot 원 수량 r.avail 을 보낸다 — 서버가 다시 차감한다).
   * lot 키가 없는 탭(공정/자원)은 그대로 통과.
   */
  const withLotAvail = (tab, r) => {
    const lotKeyField = META[tab]?.lotKeyField;
    if (!lotKeyField) return r;

    const lotKey = r?.[lotKeyField];
    // 집계에 없는 lot(타 수요 할당 없음)이거나 lot 키가 없으면 차감 0 — 원 수량이 그대로 가용 수량이다
    const other = lotKey === null || lotKey === undefined
      ? 0
      : Number(otherAlloc?.[tab]?.[String(lotKey)]) || 0;
    const avail = Number(r?.avail) || 0;
    return { ...r, otherQty: other, availNet: Math.max(0, avail - other) };
  };

  // 재고/재공 자동 배분 — 자재별로 남은 소요량까지 앞 행부터 채운다.
  // 남은 소요량은 총량 기준(소요량 − 다른 탭이 이미 할당한 수량)이라,
  // 재고에서 배분한 뒤 재공에서 다시 눌러도 같은 자재를 중복으로 채우지 않는다.
  // 행별 상한은 타 수요 할당을 뺀 가용 수량(availNet)이다.
  const autoDistributeByBom = () => {
    const rowsByItem = {};
    rows.forEach((r) => {
      if (!r.active) return;
      if (!rowsByItem[r.itemCd]) rowsByItem[r.itemCd] = [];
      rowsByItem[r.itemCd].push(r);
    });

    const alloc = {};
    Object.keys(rowsByItem).forEach((itemCd) => {
      let remaining = Math.max(0, reqQtyOf(itemCd) - allocByOtherTabs(itemCd));
      rowsByItem[itemCd].forEach((r) => {
        const availNet = withLotAvail(activeTab, r).availNet ?? (Number(r.avail) || 0);
        const give = Math.max(0, Math.min(availNet, remaining));
        alloc[r.id] = give;
        remaining -= give;
      });
    });

    setRows(rows.map((r) => ({ ...r, qty: r.active ? (alloc[r.id] ?? 0) : 0 })));
  };

  const onDragStart = (idx) => () => { dragIndexRef.current = idx; };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from == null || from === idx) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    setRows(next);
  };

  const registeredKeys = useMemo(() => new Set(rows.map((r) => r[meta.keyField])), [rows, meta.keyField]);
  // 모든 탭의 후보는 API 조회 결과다(null 은 조회 전)
  const catalog = useMemo(() => (
    open ? (remoteCatalog[activeTab] ?? []) : []
  ), [open, activeTab, remoteCatalog]);
  // 등록된 현재고의 실적 일자 — 라벨은 이 한 건만 표시한다(이동중 재고의 계획 기준일자는 표시하지 않음).
  // 조회 결과 자체가 단일 일자지만, 기준일자를 바꿔가며 등록해 섞였다면 가장 최근 일자를 쓴다.
  const snapshotTxnDate = useMemo(() => (
    [...new Set(rows.map((r) => r.txnDate).filter(Boolean))].sort().pop() ?? ""
  ), [rows]);

  // 검색어는 조회 후보의 모든 표시 컬럼 값을 대상으로 한다(전체 열 검색)
  const filteredCatalog = useMemo(() => {
    const q = lookupQuery.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((c) => Object.values(c).some((v) => String(v ?? "").toLowerCase().includes(q)));
  }, [catalog, lookupQuery]);

  const openLookup = () => { setLookupSel({}); setLookupQuery(""); setLookupOpen(true); };
  const closeLookup = () => setLookupOpen(false);
  // 기준일자가 바뀌면 후보 목록 자체가 달라지므로 선택도 함께 초기화한다
  const changeBaseDate = (v) => { setBaseDate(v); setLookupSel({}); };
  const toggleLookupSel = (key) => setLookupSel((s) => ({ ...s, [key]: !s[key] }));

  // 등록 가능(미등록) 후보 — 전체선택 헤더는 검색으로 보이는 행만 대상, 선택 건수/등록은 검색과 무관하게 그동안 고른 전체를 대상으로 한다
  const allAvail = catalog.filter((c) => !registeredKeys.has(c[meta.keyField]));
  const visibleAvail = filteredCatalog.filter((c) => !registeredKeys.has(c[meta.keyField]));
  const lookupAllChecked = visibleAvail.length > 0 && visibleAvail.every((c) => lookupSel[c[meta.keyField]]);
  const toggleLookupAll = () => {
    setLookupSel((s) => {
      const next = { ...s };
      const check = !lookupAllChecked;
      visibleAvail.forEach((c) => { next[c[meta.keyField]] = check; });
      return next;
    });
  };
  const lookupSelCount = allAvail.filter((c) => lookupSel[c[meta.keyField]]).length;

  const registerSelected = () => {
    const additions = allAvail
      .filter((c) => lookupSel[c[meta.keyField]])
      .map((c) => ({ id: `${activeTab}-${++uidRef.current}`, ...c, qty: 0, active: true }));
    if (additions.length) setRows([...rows, ...additions]);
    setLookupOpen(false);
    setLookupSel({});
    setLookupQuery("");
  };

  // 소요량을 넘겨 할당된 자재 — min 환산에 가려 잔여가 0으로 보이더라도 저장은 서버에서 거부되므로 미리 알린다
  const exceededItems = useMemo(() => {
    if (!meta.allocByBom) return [];
    return Object.keys(allocByItem).filter((itemCd) => {
      const reqQty = (Number(bomRatio?.[itemCd]) || 0) * orderQty;
      return reqQty > 0 && allocByItem[itemCd] - reqQty > QTY_EPSILON;
    });
  }, [meta.allocByBom, allocByItem, bomRatio, orderQty]);

  // 요약 — mode "sum": 주문 수량 대비 할당 합계/잔여, mode "none": 지정 건수만
  const summary = useMemo(() => {
    if (meta.mode === "none") {
      const activeCount = rows.filter((r) => r.active).length;
      return { kind: "count", total: rows.length, active: activeCount };
    }

    // 현재 탭이 넣은 할당 수량 합계 — 환산 없이 입력값 그대로(어느 탭에서 얼마를 채웠는지 확인용)
    const thisTab = rows.filter((r) => r.active).reduce((a, r) => a + (Number(r.qty) || 0), 0);

    // BOM 기준 탭은 자재 수량을 그대로 더하지 않고 완제품 환산 수량으로 본다.
    // 할당된 자재 중 가장 부족한 자재가 생산 가능 수량을 결정하므로 min(할당량 / 단위 소요량),
    // 완제품 개수이므로 내림 처리한다. (예: 단위 소요량 B 2 / C 3 에 30·30 할당 → min(15, 10) = 10)
    // 할당량은 재고·재공 총량이므로 두 탭에서 같은 환산 수량이 표시된다.
    if (meta.allocByBom) {
      let coverage = null;
      Object.keys(allocByItem).forEach((itemCd) => {
        const qty = allocByItem[itemCd];
        const unitQty = Number(bomRatio?.[itemCd]) || 0;
        // 할당이 없는 자재, 환산 정보가 없는 자재는 환산 대상에서 제외
        if (qty <= 0 || unitQty <= 0) return;
        const cover = qty / unitQty;
        coverage = coverage === null ? cover : Math.min(coverage, cover);
      });
      const allocated = coverage === null ? 0 : Math.floor(coverage);
      return { kind: "sum", allocated, remain: orderQty - allocated, thisTab };
    }

    return { kind: "sum", allocated: thisTab, remain: orderQty - thisTab };
  }, [rows, meta.mode, meta.allocByBom, allocByItem, bomRatio, orderQty]);

  // 저장 — 네 탭(재고·재공·공정·자원) 지정 내역을 z_mp_demand_alloc(alloc_obj)에 반영한다.
  const handleSave = () => {
    // 주문 유형(orderType)은 원천 데이터에서 비어 있을 수 있어 필수로 보지 않는다(서버가 빈 값으로 저장)
    const dueDate = dateUtils.formatDate(order?.dueDate, "yyyy-MM-dd");
    if (!order?.locCd || !order?.itemCd || !dueDate) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
      debug("[MpDemandConstraint] save aborted - missing order key", order);
      return;
    }
    // 수요 버전이 없으면 저장 대상 계획을 특정할 수 없다 — 서버에서도 같은 조건으로 막는다
    if (!parPlanVerId) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoVersion") });
      return;
    }

    const payload = {
      parPlanVerId,
      locCd: order.locCd,
      itemCd: order.itemCd,
      dueDate,
      // 수요타입에 따라 order_type 을 DP(수요계획)/RP(보충계획)/PO(생산 주문)로 저장한다.
      orderType: resolveOrderType(order),
      reqLocCd: order.reqLocCd ?? null,
      customerCd: order.customerCd ?? null,
      orderQty,
      // availDate = 재고가 가용해지는 일자 — 현재고는 실적 일자, 이동중 재고는 도착 일자
      inventory: data.inventory.map((r) => ({
        invId: r.invId ?? null,
        locCd: r.locCd,
        itemCd: r.itemCd,
        availDate: r.availDate || null,
        availQty: Number(r.avail) || 0,
        allocQty: Number(r.qty) || 0,
        activeFlg: !!r.active,
      })),
      wip: data.wip.map((r) => ({
        wipId: r.wipId ?? null,
        // 원천이 NULL 인 재공 코드는 표시용 "-" 로 들고 있으므로 저장 시 다시 null 로 되돌린다
        wipCd: toSavedCode(r.wipCd),
        locCd: r.locCd,
        wcCd: r.workcenter,
        itemCd: r.itemCd,
        routeCd: r.route,
        resCd: r.resource,
        prodDate: r.prodDate || null,
        availQty: Number(r.avail) || 0,
        allocQty: Number(r.qty) || 0,
        activeFlg: !!r.active,
      })),
      // 공정·자원은 지정한 순서가 우선순위 — 화면 순서를 1부터 매겨 저장한다.
      // routeName/resourceName(명칭)은 저장하지 않는다 — 복원 시 코드로 마스터를 다시 조회해 표시명을 채운다.
      route: data.route.map((r, idx) => ({
        routeCd: r.code,
        locCd: r.locCd,
        resCnt: Number(r.resCnt) || 0,
        priority: idx + 1,
        activeFlg: !!r.active,
      })),
      resource: data.resource.map((r, idx) => ({
        routeCd: r.route,
        resCd: r.code,
        wcCd: r.workcenter,
        locCd: r.locCd,
        priority: idx + 1,
        activeFlg: !!r.active,
      })),
    };

    setSaving(true);
    mpDemandConstraintService.saveAllocation(payload)
      .then(() => {
        setSaving(false);
        // 모달을 닫아도 이 컴포넌트는 계속 마운트되어 있어 Snackbar 는 그대로 노출된다
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        onClose();
      })
      .catch((e) => {
        setSaving(false);
        const message = e?.response?.data?.message;
        setSnackInfo({
          open: true,
          severity: snackBarMessageColor.saveFail,
          content: message ? t(message) : t("MSG_SaveFailed"),
        });
        debug(e);
      });
  };

  return (
    <>
      <BaseConfigModal
        open={open}
        onClose={onClose}
        title={t("mpDemandConstraintSetting")}
        onSave={handleSave}
        saveDisabled={saving}
        width={1000}
      >
        {/* 대상 주문 요약 */}
        <Box sx={{
          display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center",
          bgcolor: "action.hover", borderRadius: 1.5, px: 1.5, py: 1, mb: 1.5,
        }}>
          <Typography sx={{ fontSize: FS.BODY, fontWeight: 700, color: "text.secondary", mr: 0.5 }}>
            {t("targetOrder")}
          </Typography>
          {orderChips.map((c) => (
            <Chip key={c.label} size="small" variant="outlined"
              label={<><Typography component="span" sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{c.label} </Typography>
                <Typography component="span" sx={{ fontSize: FS.SUB, fontWeight: 700 }}>{c.value}</Typography></>}
              sx={{ height: 24, borderColor: "divider", bgcolor: "background.paper" }} />
          ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "stretch", gap: 1.5, minHeight: 360 }}>
          {/* 좌측 탭 레일 */}
          <Box sx={{ width: 168, flex: "none", display: "flex", flexDirection: "column", gap: 0.5 }}>
            {TAB_KEYS.map((key) => {
              const m = META[key];
              const Icon = m.icon;
              const on = key === activeTab;
              return (
                <Box key={key} component="button" type="button" onClick={() => setActiveTab(key)}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.75,
                    fontSize: FS.TITLE, fontWeight: on ? 700 : 500, fontFamily: "inherit", cursor: "pointer",
                    color: on ? "primary.main" : "text.secondary",
                    bgcolor: on ? "action.selected" : "transparent",
                    border: 1, borderColor: on ? "primary.light" : "transparent",
                    borderRadius: 1.5, px: 1.25, py: 1, textAlign: "left",
                  }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                    <Icon sx={{ fontSize: 17 }} />
                    <span>{t(m.labelKey)}</span>
                  </Box>
                  <Box component="span" sx={{
                    fontSize: FS.CAPTION, fontWeight: 700, minWidth: 18, textAlign: "center", borderRadius: 10,
                    px: 0.75, color: on ? "primary.main" : "text.disabled",
                    bgcolor: on ? "background.paper" : "action.hover",
                  }}>
                    {data[key].length}
                  </Box>
                </Box>
              );
            })}
            <Typography sx={{ fontSize: FS.BODY, lineHeight: 1.6, color: "text.disabled", mt: "auto", pt: 1.5 }}>
              {t(meta.hintKey)}
            </Typography>
          </Box>

          {/* 우측 본문 */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              {/* 기준일자는 탭에서 지정한다 — 조회 팝업은 이 시점으로 확정된 재고만 보여준다 */}
              {meta.useBaseDate && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("baseDt")}
                    views={["year", "month", "day"]}
                    format="yyyy-MM-dd"
                    openTo="day"
                    value={baseDate}
                    onChange={changeBaseDate}
                    slotProps={{
                      textField: {
                        size: "small",
                        // 오른쪽 소형 버튼(size="small", 폰트 13px)과 높이·폰트를 맞춘다.
                        // 입력값·플레이스홀더(format)·라벨 모두 13px 로 통일한다.
                        sx: {
                          width: 158, flex: "none",
                          "& .MuiInputBase-root": { fontSize: FS.TITLE },
                          "& .MuiInputBase-input": { fontSize: FS.TITLE, py: 0.75 },
                          "& .MuiInputLabel-root": { fontSize: FS.TITLE },
                          ...COMPACT_LABEL_SX,
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
              {/* 생산 자원은 대체 자원이 있는 공정을 먼저 골라야 조회된다 */}
              {meta.needsRoute && (
                <TextField select size="small" label={t("route")}
                  value={selectedRouteCd} onChange={(e) => setSelectedRouteCd(e.target.value)}
                  disabled={resourceRoutes.length === 0}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
                  // 기준일자 DatePicker 및 오른쪽 소형 버튼(size="small", 폰트 13px)과 크기를 맞춘다(폭 158 / 13px / py 0.75).
                  // Select 는 값이 input 이 아니라 .MuiSelect-select 로 그려지므로 함께 지정한다.
                  // 라벨(선택 전에는 플레이스홀더처럼 보임)도 13px 로 통일한다.
                  sx={{
                    width: 158, flex: "none",
                    "& .MuiInputBase-root": { fontSize: FS.TITLE },
                    "& .MuiInputBase-input": { fontSize: FS.TITLE, py: 0.75 },
                    "& .MuiSelect-select": { fontSize: FS.TITLE, py: 0.75, minHeight: "auto" },
                    "& .MuiInputLabel-root": { fontSize: FS.TITLE },
                    ...COMPACT_LABEL_SX,
                  }}>
                  {resourceRoutes.map((r) => (
                    <MenuItem key={r.code} value={r.code} sx={{ fontSize: FS.TITLE }}>
                      {`${r.code} (${r.resCnt})`}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <Button size="small" variant="outlined" startIcon={<SearchIcon />} onClick={openLookup}
                disabled={meta.needsRoute && !selectedRouteCd}>
                {t("lookupAndRegister", { target: t(meta.labelKey) })}
              </Button>
              <Button size="small" color="error" startIcon={<DeleteOutlineIcon />}
                disabled={!Object.values(curSel).some(Boolean)} onClick={removeSelected}>
                {t("deleteSelected")}
              </Button>
              <Box sx={{ flex: 1 }} />
              {/* 등록된 재고가 어느 실적 일자로 집계된 것인지 표시 */}
              {meta.useBaseDate && snapshotTxnDate && (
                <SnapshotChip label={t("txnDate")} value={snapshotTxnDate} />
              )}
              {meta.mode === "sum" && (
                <Button size="small" startIcon={<BoltIcon />}
                  disabled={!rows.some((r) => r.active)}
                  onClick={autoDistributeByBom}>
                  {t("autoDistribute")}
                </Button>
              )}
            </Box>

            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "auto", maxHeight: 300, flex: 1 }}>
              <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { fontSize: FS.SUB, py: 0.5, px: 1, whiteSpace: "nowrap" } }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && rows.some((r) => curSel[r.id])}
                        onChange={toggleSelectAll} disabled={rows.length === 0} />
                    </TableCell>
                    {meta.usePriority && <TableCell sx={{ width: 76, fontWeight: 700 }}>{t("priority")}</TableCell>}
                    {meta.cols.map((c) => (
                      <TableCell key={c.key} align={c.align ?? "left"} sx={{ fontWeight: 700 }}>{t(c.labelKey)}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, idx) => {
                    // 표시·상한은 타 수요 할당을 뺀 값(availNet)을 쓴다 — r 은 저장용 원본 그대로 둔다
                    const row = withLotAvail(activeTab, r);

                    return (
                    // 우선순위를 쓰지 않는 탭(Pegging)은 드래그 재정렬도 제공하지 않는다
                    <TableRow key={r.id} draggable={!!meta.usePriority}
                      onDragStart={meta.usePriority ? onDragStart(idx) : undefined}
                      onDragOver={meta.usePriority ? onDragOver : undefined}
                      onDrop={meta.usePriority ? onDrop(idx) : undefined}
                      selected={!!curSel[r.id]}
                      sx={{ cursor: meta.usePriority ? "grab" : "default", opacity: r.active === false ? 0.55 : 1 }}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={!!curSel[r.id]} onChange={() => toggleRowSel(r.id)} />
                      </TableCell>
                      {meta.usePriority && (
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <DragIndicatorIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                            {idx + 1}
                          </Box>
                        </TableCell>
                      )}
                      {meta.cols.map((c) => {
                        // 표시용 순번 — 저장 값이 아니라 화면에 보이는 순서 그대로의 시퀀스
                        if (c.type === "seq") {
                          return (
                            <TableCell key={c.key} align={c.align ?? "left"}>{idx + 1}</TableCell>
                          );
                        }
                        if (c.type === "toggle") {
                          return (
                            <TableCell key={c.key}>
                              <StyledSwitch size="small" checked={!!r.active}
                                onChange={(e) => updateCell(r.id, "active", e.target.checked)} />
                            </TableCell>
                          );
                        }
                        if (c.type === "num") {
                          // 재고/재공 할당 수량은 lot 가용 수량을 초과할 수 없음 —
                          // 다른 수요가 이미 가져간 몫(otherQty)을 뺀 availNet 이 상한이다
                          const lotCap = row.availNet ?? row.avail;
                          let max = c.key === "qty" && lotCap !== undefined ? Number(lotCap) || 0 : undefined;
                          // BOM 기준 탭은 자재별 소요량(주문 수량 × 단위 소요량)도 상한 —
                          // 상한 계산의 기준은 재고·재공 총량이라, 같은 자재를 다른 탭에서 이미 할당했으면
                          // 그만큼 뺀 잔여만 허용한다(같은 탭의 다른 행도 동일하게 차감된다)
                          if (c.key === "qty" && meta.allocByBom) {
                            const reqQty = reqQtyOf(r.itemCd);
                            if (reqQty > 0) {
                              const usedByOthers = (allocByItem[r.itemCd] ?? 0) - (r.active ? Number(r.qty) || 0 : 0);
                              const bomCap = Math.max(0, reqQty - usedByOthers);
                              max = max === undefined ? bomCap : Math.min(max, bomCap);
                            }
                          }
                          // 이미 저장된 할당이 소요량을 넘는 경우(BOM 변경 등) — 입력칸으로 표시해 줄이도록 유도한다.
                          // 저장 이후 다른 수요가 같은 lot 을 채워 가용 수량을 넘긴 경우도 같은 방식으로 표시한다
                          // (자동으로 줄이지 않는다 — 그대로 저장하면 서버가 400 으로 거부한다)
                          const overLot = c.key === "qty" && row.availNet !== undefined
                            && (Number(row.qty) || 0) - row.availNet > QTY_EPSILON;
                          const exceeded = (c.key === "qty" && exceededItems.includes(r.itemCd)) || overLot;
                          return (
                            <TableCell key={c.key} align={c.align ?? "left"}>
                              <TextField size="small" type="number" value={r[c.key] ?? 0} error={exceeded}
                                inputProps={{ min: 0, ...(max !== undefined ? { max } : {}) }}
                                onChange={(e) => {
                                  let v = Number(e.target.value);
                                  if (Number.isNaN(v)) v = 0;
                                  v = Math.max(0, v);
                                  if (max !== undefined) v = Math.min(v, max);
                                  updateCell(r.id, c.key, v);
                                }}
                                sx={{ width: 88, "& .MuiInputBase-input": { fontSize: FS.SUB, py: 0.5, textAlign: "right" } }} />
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={c.key} align={c.align ?? "left"}>{row[c.key] ?? "-"}</TableCell>
                        );
                      })}
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {rows.length === 0 && (
                <Box sx={{ py: 5, textAlign: "center", fontSize: FS.TITLE, color: "text.disabled" }}>
                  {t("MSG_NoRegisteredItems", { target: t(meta.labelKey) })}
                </Box>
              )}
            </Box>

            {/* 요약 바 */}
            <Box sx={{
              display: "flex", alignItems: "center", gap: 2.5, mt: 1.25, px: 2, py: 1,
              bgcolor: "action.hover", borderRadius: 1.5,
            }}>
              {summary.kind === "sum" ? (
                <>
                  <SummaryStat label={t("orderQty")} value={orderQty} />
                  <Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />
                  {/* 할당/잔여는 재고·재공 총량 기준이라 두 탭에서 같은 값이다 — 탭별 기여분은 오른쪽에 따로 표시 */}
                  <SummaryStat label={t(meta.allocByBom ? "allocatedQtyByFg" : "allocatedQty")}
                    value={summary.allocated} color="primary.main" />
                  <Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />
                  <SummaryStat label={t("remainingQty")} value={summary.remain}
                    color={summary.remain < 0 ? "error.main" : summary.remain === 0 ? "primary.main" : "text.primary"} />
                  {meta.allocByBom && (
                    <>
                      <Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />
                      <SummaryStat label={t("allocatedQtyThisTab")} value={summary.thisTab} />
                    </>
                  )}
                  <Box sx={{ flex: 1 }} />
                  {/* 소요량을 넘긴 자재가 있으면 잔여가 0으로 보여도 저장이 거부된다 — 먼저 알린다 */}
                  {exceededItems.length > 0 && (
                    <Chip size="small" variant="outlined" color="error" icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
                      title={t("MSG_AllocExceedsRequirement")}
                      label={t("allocExceedCount", { count: exceededItems.length })}
                      sx={{ fontWeight: 700 }} />
                  )}
                  <StatusChip remain={summary.remain} t={t} />
                </>
              ) : (
                <>
                  <SummaryStat label={t(meta.labelKey)} value={summary.total} />
                  <Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />
                  <SummaryStat label={t("activeFlg")} value={summary.active} color="primary.main" />
                </>
              )}
            </Box>
          </Box>
        </Box>
      </BaseConfigModal>

      {/* 조회하여 등록 — 선택 팝업. 등록은 부모 그리드에 행을 넣을 뿐 저장하지 않는다(DoneAll) */}
      <BaseConfigModal
        open={lookupOpen}
        onClose={closeLookup}
        title={t("lookupAndRegister", { target: t(meta.labelKey) })}
        subtitle={`${t("MSG_SelectTargetToAssign", { target: t(meta.labelKey) })}`
          + (meta.useBaseDate ? ` ${t("MSG_LookupAsOfBaseDate")}` : "")
          + (meta.bomScoped ? ` ${t("MSG_BomComponentScope")}` : "")}
        onSave={registerSelected}
        saveLabel={`${t("register")} (${lookupSelCount})`}
        saveIcon={<DoneAllIcon />}
        saveDisabled={lookupSelCount === 0 || lookupLoading}
        extraActions={
          <Typography sx={{ fontSize: FS.SUB, color: "text.secondary" }}>
            {t("MSG_SelectedCount", { count: lookupSelCount })}
          </Typography>
        }
        width={760}>
          <TextField size="small" value={lookupQuery} onChange={(e) => setLookupQuery(e.target.value)}
            placeholder={t("search")} fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ fontSize: 18, color: "text.disabled", mr: 0.5 }} />,
              endAdornment: lookupQuery ? (
                <IconButton size="small" onClick={() => setLookupQuery("")} aria-label="clear search" sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                </IconButton>
              ) : null,
            }}
            sx={{ mb: 1, "& .MuiInputBase-input": { fontSize: FS.TITLE, py: 0.75 } }} />
          <Box sx={{ maxHeight: Math.min(filteredCatalog.length, 8) * ROW_H + ROW_H, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { fontSize: FS.SUB, py: 0.5, px: 1, whiteSpace: "nowrap" } }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={lookupAllChecked} onChange={toggleLookupAll}
                      disabled={visibleAvail.length === 0 || lookupLoading} />
                  </TableCell>
                  {meta.cols.filter((c) => c.type !== "toggle" && c.key !== "qty").map((c) => (
                    <TableCell key={c.key} align={c.align ?? "left"} sx={{ fontWeight: 700 }}>{t(c.labelKey)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* 조회 중/실패 상태에서는 직전 결과를 그대로 두지 않고 안내 문구만 보여준다 */}
                {(lookupLoading || lookupError ? [] : filteredCatalog).map((c, idx) => {
                  const key = c[meta.keyField];
                  const registered = registeredKeys.has(key);
                  // 등록 전 후보도 등록 후 그리드와 같은 가용 수량(타 수요 할당 차감)을 보여준다.
                  // 등록은 원본 c 로 하므로(registerSelected) 계산값이 data 에 굳지 않는다.
                  const cand = withLotAvail(activeTab, c);
                  return (
                    <TableRow key={key} selected={!!lookupSel[key]} sx={{ opacity: registered ? 0.5 : 1 }}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={!!lookupSel[key]} disabled={registered} onChange={() => toggleLookupSel(key)} />
                      </TableCell>
                      {meta.cols.filter((col) => col.type !== "toggle" && col.key !== "qty").map((col) => (
                        <TableCell key={col.key} align={col.align ?? "left"}>
                          {col.type === "seq" ? idx + 1 : cand[col.key] ?? "-"}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {(lookupLoading || lookupError || filteredCatalog.length === 0) && (
              <Box sx={{ py: 4, textAlign: "center", fontSize: FS.TITLE, color: lookupError ? "error.main" : "text.disabled" }}>
                {/* 자원 탭은 공정을 먼저 선택해야 조회 대상이 생긴다 */}
                {lookupLoading ? t("MSG_Loading")
                  : lookupError
                    || (meta.needsRoute && !selectedRouteCd ? t("MSG_SelectRouteFirst") : t("MSG_NoData"))}
              </Box>
            )}
          </Box>
      </BaseConfigModal>

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

// 스냅샷 일자 표시 칩 — 등록된 재고가 어느 시점 기준인지 알려준다
const SnapshotChip = ({ label, value }) => (
  <Chip size="small" variant="outlined"
    label={<><Typography component="span" sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{label} </Typography>
      <Typography component="span" sx={{ fontSize: FS.BODY, fontWeight: 700 }}>{value}</Typography></>}
    sx={{ height: 26, borderColor: "divider", bgcolor: "background.paper" }} />
);

const SummaryStat = ({ label, value, color = "text.primary" }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
    <Typography sx={{ fontSize: FS.CAPTION, fontWeight: 600, color: "text.secondary" }}>{label}</Typography>
    <Typography sx={{ fontSize: FS.H1, fontWeight: 800, color }}>{value}</Typography>
  </Box>
);

const StatusChip = ({ remain, t }) => {
  const over = remain < 0;
  const done = remain === 0;
  const Icon = over ? ErrorOutlineIcon : CheckCircleOutlineIcon;
  const color = over ? "error" : done ? "success" : "warning";
  return (
    <Chip size="small" variant="outlined" color={color} icon={<Icon sx={{ fontSize: 16 }} />}
      label={`${t("remainingQty")} ${remain}`} sx={{ fontWeight: 700 }} />
  );
};

export default MpDemandConstraintModal;
